import React, { useState, useEffect } from "react";
import { Phone, ArrowRight, Loader2, X, ShieldCheck } from "lucide-react";
import { useSendOTP, useVerifyOTP } from "../../hooks/useAuth";
import { fetchUserSubscription } from "../../api/subscription"; // adjust path as needed

const Login = ({ isOpen, onClose, onOpenRegister }) => {
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("mobile");
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(0);

  const sendOTPMutation = useSendOTP();
  const verifyOTPMutation = useVerifyOTP();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setMobileNumber("");
      setOtp("");
      setStep("mobile");
      setError("");
      setCountdown(0);
    }
  }, [isOpen]);

  // Countdown timer
  useEffect(() => {
    let interval;
    if (countdown > 0) {
      interval = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [countdown]);

  // Close on Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && isOpen) onClose?.();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const startCountdown = () => setCountdown(60);

  const handleSendOTP = async () => {
    if (!mobileNumber || mobileNumber.length !== 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    setError("");
    try {
      const result = await sendOTPMutation.mutateAsync({
        mobile: mobileNumber,
      });
      if (result.userId) {
        localStorage.setItem("tempUserId", result.userId);
        setStep("otp");
        startCountdown();
      } else {
        setError(result.message || "Failed to send OTP");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Network error. Please try again.",
      );
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 4) {
      setError("Please enter a valid 4-digit OTP");
      return;
    }
    setError("");
    const userId =
      localStorage.getItem("tempUserId") || localStorage.getItem("userId");
    if (!userId) {
      setError("Session expired. Please try again.");
      setStep("mobile");
      return;
    }
    try {
      const data = await verifyOTPMutation.mutateAsync({ userId, otp });

      // Store auth data
      localStorage.setItem("authToken", data.token);
      localStorage.setItem("userId", data.userId);
      localStorage.removeItem("tempUserId");
      localStorage.removeItem('userSubscription')

      

      // Fetch subscription and store in localStorage
      try {
        const subscription = await fetchUserSubscription();
        if (subscription) {
          localStorage.setItem("userSubscription", JSON.stringify(subscription));
        } else {
          localStorage.removeItem("userSubscription");
        }
      } catch (subErr) {
        console.warn("Could not fetch subscription:", subErr);
        // Subscription fetch fail thay to login rok nahi
      }

      // Check profile status
      const isProfileCreated = localStorage.getItem("isProfileCreated");
      if (isProfileCreated === "true") {
        onClose?.();
        window.dispatchEvent(new Event("userUpdated"));
      } else {
        // Profile incomplete — open Register modal
        onClose?.();
        onOpenRegister?.();
        window.dispatchEvent(new Event("userUpdated"));
      }
    } catch (err) {
      setError(err.response?.data?.message || "Invalid OTP. Please try again.");
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    setError("");
    try {
      const result = await sendOTPMutation.mutateAsync({
        mobile: mobileNumber,
      });
      if (result.userId) {
        localStorage.setItem("tempUserId", result.userId);
        startCountdown();
      } else {
        setError(result.message || "Failed to resend OTP");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Network error. Please try again.",
      );
    }
  };

  const handleChangeNumber = () => {
    setStep("mobile");
    setOtp("");
    setError("");
    localStorage.removeItem("tempUserId");
  };

  const isLoading = sendOTPMutation.isPending || verifyOTPMutation.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      {/* Backdrop - Lightened */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />

      {/* Modal - Light Theme */}
      <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X size={20} />
        </button>

        <div className="px-8 py-8">
          {/* Icon + Title */}
          <div className="flex flex-col items-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-[#BBE520]/10 border border-[#BBE520]/30 flex items-center justify-center mb-3">
              {step === "mobile" ? (
                <Phone size={24} className="text-[#7a9c14]" />
              ) : (
                <ShieldCheck size={24} className="text-[#7a9c14]" />
              )}
            </div>
            <h2 className="text-xl font-bold text-gray-900 tracking-tight">
              {step === "mobile" ? "Welcome back" : "Verify OTP"}
            </h2>
            <p className="text-xs text-gray-500 mt-1 text-center">
              {step === "mobile"
                ? "Enter your mobile number to continue"
                : `Code sent to +91 ${mobileNumber}`}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-600 text-xs text-center">{error}</p>
            </div>
          )}

          {step === "mobile" ? (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">
                  Mobile Number
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#7a9c14]">
                    +91
                  </span>
                  <input
                    type="tel"
                    value={mobileNumber}
                    onChange={(e) =>
                      setMobileNumber(
                        e.target.value.replace(/\D/g, "").slice(0, 10),
                      )
                    }
                    placeholder="00000 00000"
                    className="w-full pl-14 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#BBE520] focus:ring-2 focus:ring-[#BBE520]/20 transition-all text-sm tracking-widest"
                    autoFocus
                    disabled={isLoading}
                  />
                </div>
              </div>

              <button
                onClick={handleSendOTP}
                disabled={isLoading}
                className="w-full bg-[#BBE520] text-gray-900 py-3 rounded-xl font-bold text-sm hover:bg-[#cef72a] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 tracking-wide"
              >
                {sendOTPMutation.isPending ? (
                  <>
                    <Loader2 className="animate-spin" size={16} /> Sending...
                  </>
                ) : (
                  <>
                    Send OTP <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">
                  Enter 4-digit OTP
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))
                  }
                  placeholder="· · · ·"
                  className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-center text-2xl tracking-[1rem] font-mono focus:outline-none focus:border-[#BBE520] focus:ring-2 focus:ring-[#BBE520]/20 transition-all placeholder-gray-300"
                  maxLength={4}
                  autoFocus
                  disabled={isLoading}
                />
              </div>

              <button
                onClick={handleVerifyOTP}
                disabled={isLoading}
                className="w-full bg-[#BBE520] text-gray-900 py-3 rounded-xl font-bold text-sm hover:bg-[#cef72a] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 tracking-wide"
              >
                {verifyOTPMutation.isPending ? (
                  <>
                    <Loader2 className="animate-spin" size={16} /> Verifying...
                  </>
                ) : (
                  <>
                    Verify & Continue <ArrowRight size={16} />
                  </>
                )}
              </button>

              <div className="flex items-center justify-between pt-1">
                <button
                  onClick={handleChangeNumber}
                  disabled={isLoading}
                  className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                >
                  ← Change number
                </button>
                <button
                  onClick={handleResendOTP}
                  disabled={countdown > 0 || isLoading}
                  className={`text-xs font-medium transition-colors ${
                    countdown > 0 || isLoading
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-[#7a9c14] hover:text-[#9ecf1a]"
                  }`}
                >
                  {countdown > 0 ? `Resend in ${countdown}s` : "Resend OTP"}
                </button>
              </div>
            </div>
          )}

          <p className="text-center text-[10px] text-gray-400 mt-6">
            By continuing, you agree to our Terms & Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;